import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { X, ChevronDown } from 'lucide-react';

const MultiUserSelect = ({ values = [], onChange, placeholder = "Select Owners", className = "", disabled = false }) => {
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: api.getUsers,
  });

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div className={`w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 animate-pulse text-sm ${className}`}>
        Loading users...
      </div>
    );
  }

  // Ensure values is an array
  const safeValues = Array.isArray(values) ? values : (typeof values === 'string' ? JSON.parse(values || '[]') : []);
  const selectedUsers = users.filter(u => safeValues.includes(u.id));

  const toggleUser = (userId) => {
    if (disabled) return;
    if (safeValues.includes(userId)) {
      onChange(safeValues.filter(id => id !== userId));
    } else {
      onChange([...safeValues, userId]);
    }
  };

  const removeUser = (e, userId) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(safeValues.filter(id => id !== userId));
  };

  return (
    <div className={`relative ${className} ${disabled ? 'opacity-80' : ''}`} ref={containerRef}>
      <div 
        className={`form-input flex flex-wrap items-center gap-1.5 py-1.5 px-2 transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-base)', minHeight: '40px' }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedUsers.length === 0 && <span className="text-muted ml-1" style={{ fontSize: '0.875rem' }}>{placeholder}</span>}
        {selectedUsers.map(user => (
          <span 
            key={user.id}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
            style={{ 
              backgroundColor: 'var(--accent-dim)', 
              color: 'var(--accent)', 
              border: '1px solid var(--accent)',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap'
            }}
          >
            {user.full_name}
            {!disabled && (
              <button 
                type="button"
                className="hover:text-red-400 focus:outline-none transition-colors" 
                onClick={(e) => removeUser(e, user.id)}
              >
                <X size={12} strokeWidth={3} />
              </button>
            )}
          </span>
        ))}
        {!disabled && <div className="ml-auto opacity-50"><ChevronDown size={14} /></div>}
      </div>

      {isOpen && !disabled && (
        <div 
          className="absolute z-50 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-y-auto" 
          style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--border)' }}
        >
          {users.map((user) => (
            <div 
              key={user.id}
              className="flex items-center px-3 py-2.5 cursor-pointer transition-colors hover:opacity-80"
              style={{ borderBottom: '1px solid var(--border-light)', color: 'var(--text)', backgroundColor: safeValues.includes(user.id) ? 'var(--bg-base)' : 'transparent' }}
              onClick={() => toggleUser(user.id)}
            >
              <input 
                type="checkbox" 
                checked={safeValues.includes(user.id)}
                readOnly
                className="mr-3 cursor-pointer h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium leading-none mb-1">{user.full_name}</span>
                <span className="text-xs opacity-60 leading-none">{user.role}</span>
              </div>
            </div>
          ))}
          {users.length === 0 && <div className="p-3 text-sm text-muted">No users found.</div>}
        </div>
      )}
    </div>
  );
};

export default MultiUserSelect;
